import {
  openAppWithStoreFallback,
  mobileStoreUrl,
  APP_STORE_URL,
  PLAY_STORE_URL,
} from '../../utils/appStoreHandoff';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
} from '@mui/material';
import appStoreQr from './assets/app-store-qr.png';
import googlePlayQr from './assets/google-play-qr.png';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import { useSettings } from '../settings/shared';
import { useUserStore } from '../../stores/userStore';
import { useExpandedText } from '../notifications/useExpandedText';
import { SupportMark } from '../badges/BadgeIcon';
import '../badges/badges.css';
import baseline from '../../app-ui/baseline';
import { supportApi } from './api';
import reference from './reference.json';
import icons from './icons.json';
import useSupportMember from './useSupportMember';
import MemberStatus from './MemberStatus';
import { publicPolicy, publicProducts, rosterColor } from './model';
import './Support.css';
function SupportIcon({ name, size = 20 }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icons[name].map(([tag, props], i) => React.createElement(tag, { ...props, key: i }))}
    </svg>
  );
}
function useSupport() {
  const settings = useSettings();
  const { locale, dark } = settings;
  const colors = baseline.tokens[dark ? 'darkColors' : 'lightColors'];
  const t = (key, vars = {}) =>
    String(reference.copy[locale === 'en' ? 'en' : 'ko'][key] || key).replace(
      /\{\{(\w+)\}\}/g,
      (_, k) => vars[k] ?? '',
    );
  return {
    ...settings,
    colors,
    t,
    style: {
      ...settings.style,
      '--support-selected': colors.accentSelected,
      '--support-muted': colors.surfaceMuted,
      '--support-surface': colors.surface,
      '--support-body': colors.reviewBody,
    },
  };
}
function Header({ title, t, home }) {
  const nav = useNavigate();
  return (
    <header className="pp-settings-header">
      <button
        type="button"
        aria-label={t('common.back')}
        onClick={() =>
          window.history.state?.idx > 0 ? nav(-1) : nav(home ? '/mypage/settings' : '/sponsorship')
        }
      >
        <FiChevronLeft size={22} />
      </button>
      <h1>{title}</h1>
    </header>
  );
}
function Retry({ query, children, t }) {
  return query.isError ? (
    <div className="pp-support-error" role="alert">
      <p>{children}</p>
      <button onClick={() => query.refetch()} disabled={query.isFetching}>
        {t('common.retry')}
      </button>
    </div>
  ) : null;
}
export default function SupportPage({ page, benefits = false }) {
  const kind = page || (benefits ? 'benefits' : 'overview');
  const context = useSupport();
  const user = useUserStore((s) => s.user);
  const identity = useUserStore((s) => `${s.user?.id}:${s.user?.role}:${s.isAdmin}:${s.isLogin}`);
  const expanded = useExpandedText();
  const title = context.t(
    kind === 'overview'
      ? 'sponsor.title'
      : kind === 'benefits'
        ? 'sponsor.benefitsTitle'
        : kind === 'roster'
          ? 'sponsor.rosterTitle'
          : 'sponsor.visionTitle',
  );
  return (
    <PageLayout paddingTop={0} paddingBottom="32px">
      <SEO title={title} />
      <div
        className={'pp-settings pp-support-page' + (expanded ? ' is-expanded' : '')}
        style={context.style}
      >
        <Header title={title} t={context.t} home={kind === 'overview'} />
        {kind === 'overview' ? (
          <Overview {...context} />
        ) : kind === 'benefits' ? (
          <Benefits key={identity} {...context} user={user} />
        ) : kind === 'vision' ? (
          <Vision {...context} />
        ) : (
          <Roster {...context} />
        )}
      </div>
    </PageLayout>
  );
}
function useCatalog(locale) {
  return useQuery({
    queryKey: ['sponsorships', 'catalog', locale],
    queryFn: ({ signal }) => supportApi.catalog(locale, signal),
    staleTime: 300000,
    retry: false,
  });
}
function Overview({ locale, t, en }) {
  const cancelHandoff = useRef(null);
  const [installOpen, setInstallOpen] = useState(false);
  useEffect(() => () => cancelHandoff.current?.(), []);
  const openSupport = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    cancelHandoff.current?.();
    if (!mobileStoreUrl()) {
      setInstallOpen(true);
      return;
    }
    cancelHandoff.current = openAppWithStoreFallback('postpop://sponsorship');
  };
  const catalog = useCatalog(locale);
  const [kind, setKind] = useState('MONTHLY'),
    [amount, setAmount] = useState(10000);
  const products = publicProducts(catalog.data).filter((p) => p.kind === kind);
  const selected = products.find((p) => p.amount === amount) || products[0];
  useEffect(() => {
    if (selected && selected.amount !== amount) setAmount(selected.amount);
  }, [selected, amount]);
  const price = (p) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: p.currency,
      maximumFractionDigits: 0,
    }).format(p.amount);
  return (
    <>
      <section className="pp-support-hero">
        <span className="pp-support-hero-icon">
          <SupportIcon name="heart-handshake" size={25} />
        </span>
        <h2>{t('sponsor.heroTitle')}</h2>
        <p>{t('sponsor.heroBody')}</p>
        <Link className="pp-support-inline" to="/sponsorship/vision">
          {t('sponsor.visionLink')}
          <FiChevronRight size={17} />
        </Link>
      </section>
      <div
        role="group"
        aria-label={en ? 'Support type' : '후원 방식'}
        className="pp-support-segment"
      >
        {['MONTHLY', 'ONE_TIME'].map((k) => (
          <button key={k} aria-pressed={kind === k} onClick={() => setKind(k)}>
            {t(k === 'MONTHLY' ? 'sponsor.monthly' : 'sponsor.oneTime')}
          </button>
        ))}
      </div>
      <section className="pp-support-section">
        <h2 className="pp-support-small-title">{t('sponsor.amount')}</h2>
        <div className="pp-support-amount-grid" role="group" aria-label={t('sponsor.amount')}>
          {products.map((p) => (
            <button
              key={p.productId}
              aria-pressed={p.productId === selected?.productId}
              onClick={() => setAmount(p.amount)}
            >
              <span>{price(p)}</span>
              {p.qualifiesForPartner && <small>{t('sponsor.partnerThreshold')}</small>}
            </button>
          ))}
        </div>
        <Retry query={catalog} t={t}>
          {en
            ? 'Support options could not be refreshed. Reference amounts are shown.'
            : '후원 정보를 갱신하지 못해 기본 안내 금액을 표시합니다.'}
        </Retry>
      </section>
      <Link className="pp-support-benefit-link" to="/sponsorship/benefits">
        <span className="pp-support-benefit-icon">
          <SupportIcon name="sparkles" size={20} />
        </span>
        <span>
          <strong>{t('sponsor.benefitsTitle')}</strong>
          <small>{t('sponsor.benefitsSummary')}</small>
        </span>
        <FiChevronRight size={20} />
      </Link>
      <section className="pp-support-notice">
        <strong>
          {t(kind === 'MONTHLY' ? 'sponsor.monthlyBilling' : 'sponsor.oneTimeBilling')}
        </strong>
        <p>{t(kind === 'MONTHLY' ? 'sponsor.monthlyBillingBody' : 'sponsor.oneTimeBillingBody')}</p>
      </section>
      <div className="pp-support-app-action">
        {catalog.data?.notice && <p role="status">{catalog.data.notice}</p>}
        <p>
          {en
            ? 'Payment and subscription management are available in the Postpop app.'
            : '결제와 구독 관리는 포스트팝 앱에서 진행합니다.'}
        </p>
        <a href="postpop://sponsorship" className="pp-support-open-app" onClick={openSupport}>
          {en ? 'View support in the app' : '앱에서 후원 확인'}
        </a>
      </div>
      <Dialog
        open={installOpen}
        onClose={() => setInstallOpen(false)}
        fullWidth
        maxWidth="sm"
        className="pp-review-dialog"
        aria-labelledby="support-install-title"
      >
        <DialogTitle id="support-install-title">
          {en ? 'Open Postpop on your phone' : '휴대폰에서 포스트팝을 열어주세요'}
        </DialogTitle>
        <DialogContent>
          <p>
            {en
              ? 'Scan the QR code for your phone with its camera to open the store. After installing Postpop, open Support in the app.'
              : '휴대폰 카메라로 기기에 맞는 QR 코드를 스캔해 주세요. 스토어에서 포스트팝을 설치한 뒤 앱에서 후원하기를 열 수 있어요.'}
          </p>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 3,
              mt: 3,
            }}
          >
            {[
              {
                name: 'App Store',
                device: 'iPhone · iPad',
                url: APP_STORE_URL,
                qr: appStoreQr,
                icon: FaApple,
                iconOffset: -1.5,
              },
              {
                name: 'Google Play',
                device: 'Android',
                url: PLAY_STORE_URL,
                qr: googlePlayQr,
                icon: FaGooglePlay,
                iconOffset: -1,
              },
            ].map(({ name, device, url, qr, icon: StoreIcon, iconOffset }) => (
              <Stack key={name} spacing={1.5} alignItems="center">
                <Box component="span" sx={{ fontSize: 14, color: 'text.secondary' }}>
                  {device}
                </Box>
                <Box
                  component="img"
                  src={qr}
                  alt={`${name} QR`}
                  width={180}
                  height={180}
                  sx={{ display: 'block', backgroundColor: '#fff', borderRadius: 1 }}
                />
                <Button
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={
                    <StoreIcon
                      size={18}
                      aria-hidden="true"
                      focusable="false"
                      style={{ transform: `translateY(${iconOffset}px)` }}
                    />
                  }
                  sx={{ minWidth: 180 }}
                >
                  {name}
                </Button>
              </Stack>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstallOpen(false)}>{en ? 'Close' : '닫기'}</Button>
        </DialogActions>
      </Dialog>
      <p className="pp-support-disclaimer">{t('sponsor.disclaimer')}</p>
      <Link className="pp-support-roster-link" to="/sponsorship/roster">
        {t('sponsor.rosterOpen')}
        <FiChevronRight size={16} />
      </Link>
    </>
  );
}
function Vision({ locale, t }) {
  const policy = reference.policy[locale === 'en' ? 'en' : 'ko'];
  return (
    <>
      <section className="pp-support-vision-copy">
        <span className="pp-support-eyebrow">OUR VISION</span>
        <h2>{policy.visionTitle}</h2>
        {policy.visionParagraphs.map((p) => (
          <p key={p}>{p}</p>
        ))}
      </section>
      <p className="pp-support-closing">{t('sponsor.vision.closing')}</p>
    </>
  );
}
function Avatar({ entry }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="pp-support-avatar" aria-hidden="true">
      {entry.profileImageUrl && !failed ? (
        <img src={entry.profileImageUrl} alt="" onError={() => setFailed(true)} />
      ) : (
        entry.nickname.trim().slice(0, 1).toUpperCase() || 'P'
      )}
    </span>
  );
}
function Roster({ t, dark, colors }) {
  const query = useQuery({
    queryKey: ['sponsorships', 'roster'],
    queryFn: ({ signal }) => supportApi.roster(signal),
    retry: false,
  });
  const entries = Array.isArray(query.data?.supporters)
    ? query.data.supporters.filter((e) => e?.userId != null && typeof e.nickname === 'string')
    : [];
  return (
    <div className="pp-support-roster">
      <p className="pp-support-meta">{t('sponsor.rosterPrivacy')}</p>
      {query.isPending ? (
        <p role="status">{t('common.loading')}</p>
      ) : query.isError ? (
        <Retry query={query} t={t}>
          {t('sponsor.rosterLoadError')}
        </Retry>
      ) : entries.length ? (
        <div>
          {entries.map((e) => (
            <Link
              className="pp-support-roster-row"
              key={e.userId}
              to={'/users/' + encodeURIComponent(e.nickname)}
            >
              <Avatar key={e.userId + ':' + e.profileImageUrl} entry={e} />
              <span className="pp-support-roster-copy">
                <strong style={{ color: rosterColor(e.nicknameColor, dark, colors.text) }}>
                  {e.nickname}
                </strong>
                <small className={e.tier === 'PARTNER' ? 'is-partner' : ''}>
                  {t(e.tier === 'PARTNER' ? 'sponsor.rosterPartner' : 'sponsor.rosterSupporter')}
                </small>
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <p>{t('sponsor.rosterEmpty')}</p>
      )}
    </div>
  );
}
function Benefits({ locale, t, dark, en, user }) {
  const catalog = useCatalog(locale);
  const policy = publicPolicy(catalog.data, locale);
  const member = useSupportMember(user, policy.nicknameColors);
  const mine = member.status,
    marks = member.badges;
  const status = mine.isError ? null : mine.data;
  const mark = marks.isError ? null : marks.data;
  const [expanded, setExpanded] = useState(false);
  const supporter = status?.isSupporter === true;
  const entitled = !mine.isFetching && !mine.isError && status?.nicknameColorEntitled === true;
  const selected = status?.preferences?.selectedNicknameColorKey ?? null;
  const selectedColor = entitled ? policy.nicknameColors.find((c) => c.key === selected) : null;
  return (
    <>
      <p className="pp-support-intro">{t('sponsor.benefitsIntro')}</p>
      {user && mine.isPending && <p role="status">{t('common.loading')}</p>}
      {user && (
        <Retry query={mine} t={t}>
          {en ? 'Could not load your support status.' : '후원 상태를 불러오지 못했습니다.'}
        </Retry>
      )}
      <MemberStatus
        status={status}
        mark={mark}
        policy={policy}
        locale={locale}
        t={t}
        icon={<SupportIcon name="award" size={23} />}
      />
      <Retry query={catalog} t={t}>
        {en
          ? 'The catalog could not be refreshed. App reference guidance is shown.'
          : '안내를 갱신하지 못해 앱에 포함된 기준 안내를 표시합니다.'}
      </Retry>
      <section className="pp-support-section">
        <div className="pp-support-tier-toggle">
          <h2>{t('sponsor.tiers')}</h2>
          {supporter && (
            <button aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
              {t(expanded ? 'sponsor.hideTierDetails' : 'sponsor.showTierDetails')}
              <FiChevronRight
                size={17}
                style={{ transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)' }}
              />
            </button>
          )}
        </div>
        {(!user || !mine.isPending) && (!supporter || expanded) && (
          <div className="pp-support-tiers">
            {policy.tiers.map((tier) => (
              <article
                className={'pp-support-tier ' + (tier.key === 'PARTNER' ? 'is-partner' : '')}
                key={tier.key}
              >
                <div className="pp-support-tier-heading">
                  <span className="pp-support-tier-icon">
                    <SupportIcon name="award" size={24} />
                  </span>
                  <h3>{tier.name}</h3>
                  <p>{tier.summary}</p>
                </div>
                <div className="pp-support-eligibility">
                  <p>{t('sponsor.eligibilityEither')}</p>
                  {tier.eligibility.map((v) => (
                    <p key={v}>· {v}</p>
                  ))}
                </div>
                <ul>
                  {tier.benefits.map((v) => (
                    <li key={v}>
                      <SupportIcon name="check" size={18} />
                      <span>{v}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        )}
      </section>
      <section className="pp-support-section">
        <h2 className="pp-support-section-icon">
          <SupportIcon name="clock-3" size={21} />
          {t('sponsor.timeMark')}
        </h2>
        <p className="pp-support-meta">{t('sponsor.timeMarkBody')}</p>
        {user && marks.isPending && <p role="status">{t('common.loading')}</p>}
        {user && (
          <Retry query={marks} t={t}>
            {en ? 'Could not load your earned marks.' : '획득한 마크를 불러오지 못했습니다.'}
          </Retry>
        )}
        <div>
          {policy.milestones.map((m, i) => (
            <div className="pp-support-milestone" key={m.paidMonths}>
              <div className="pp-support-mark-rail">
                <SupportMark level={i + 1} size={44} />
                {i < policy.milestones.length - 1 && <i />}
              </div>
              <span className="pp-support-milestone-copy">
                {m.description}
                {(mark?.currentStage?.paidMonths === m.paidMonths ||
                  mark?.stages?.some((s) => s.paidMonths === m.paidMonths && s.earned)) && (
                  <small>
                    {t(
                      mark?.currentStage?.paidMonths === m.paidMonths
                        ? 'sponsor.currentMark'
                        : 'sponsor.earned',
                    )}
                  </small>
                )}
              </span>
            </div>
          ))}
        </div>
      </section>
      <section className="pp-support-section">
        <h2 className="pp-support-section-icon">
          <SupportIcon name="palette" size={21} />
          {t('sponsor.colorNickname')}
        </h2>
        <p className="pp-support-meta">{t('sponsor.colorBody')}</p>
        {user && (
          <div className="pp-support-color-preview">
            <p>{t('sponsor.colorPreview')}</p>
            <strong
              style={{ color: selectedColor?.[dark ? 'dark' : 'light'] ?? 'var(--settings-text)' }}
            >
              {user.nickname}
            </strong>
            {(status?.adminBenefitsAccess || !entitled) && (
              <p>
                {t(
                  status?.adminBenefitsAccess
                    ? 'sponsor.adminBenefitsAccess'
                    : 'sponsor.colorUnavailable',
                )}
              </p>
            )}
          </div>
        )}
        {member.failed && <p role="alert">{t('sponsor.preferenceFailed')}</p>}
        <div
          className="pp-support-palette"
          role={entitled ? 'group' : undefined}
          aria-label={t('sponsor.colorNickname')}
        >
          {(entitled
            ? [{ key: null, name: t('sponsor.colorDefault') }, ...policy.nicknameColors]
            : policy.nicknameColors
          ).map((c) => (
            <button
              key={c.key ?? 'default'}
              type="button"
              role="radio"
              aria-checked={selected === c.key}
              disabled={!entitled || member.busy}
              onClick={() => member.choose(c.key)}
              style={{ color: c[dark ? 'dark' : 'light'] ?? 'var(--settings-text)' }}
            >
              <i
                style={{
                  background: c[dark ? 'dark' : 'light'] ?? 'transparent',
                  border: c.key ? undefined : '1.5px solid var(--settings-muted)',
                }}
              />
              {c.name}
            </button>
          ))}
        </div>
        <div className="pp-support-duration">
          <strong>{t('sponsor.oneTimeColorDuration')}</strong>
          <p>
            {Object.entries(policy.nicknameColorDurationsByOneTimeAmount)
              .map(([amount, months]) =>
                t('sponsor.priceMonths', {
                  price: t('sponsor.priceWon', { amount: Number(amount).toLocaleString(locale) }),
                  count: months,
                }),
              )
              .join(' · ')}
          </p>
          <p>{t('sponsor.monthlyColorDuration')}</p>
        </div>
      </section>
      <section className="pp-support-section">
        <h2>{t('sponsor.cancellation')}</h2>
        {policy.cancellationRules.map((r) => (
          <p key={r}>· {r}</p>
        ))}
      </section>
    </>
  );
}
